import { AssetFormat, PortfolioTransactionItemType } from 'services/requests';
import { convertDate, formatUSD } from 'utils';
import { CustomColumn } from 'components/Table/Table.types';
import Currency from 'components/Currency';
import USDAmount from './components/USDAmount';
import CryptoAmount from './components/CryptoAmount';
import TransactionCell from './components/TransactionCell';

import styles from './UserTransactionsTable.module.css';

const Columns = {
  ASSET_NAME: 'assetName',
  TYPE: 'type',
  DATE: 'date',
  USD_AMOUNT: 'usdAmount',
  ASSET_AMOUNT: 'assetAmount',
  MADE_BY: 'madeBy',
  CHARGED_FROM: 'chargedFrom',
  ORDER_STATUS: 'orderStatus',
  QUOTED_PRICE: 'quotedPrice',
} as const;

export type TransactionsColumnsType = typeof Columns[keyof typeof Columns];

export type MapData = PortfolioTransactionsTableSign[];

export type PortfolioTransactionsTableSign = PortfolioTransactionItemType;

export const mapData = (
  data: PortfolioTransactionItemType[] | undefined,
): MapData | undefined =>
  data?.map((transactionItem: PortfolioTransactionItemType) => ({
    ...transactionItem,
    orderTimestamp: convertDate(transactionItem.orderTimestamp),
  }));

const columns: {
  [id in TransactionsColumnsType]: CustomColumn<PortfolioTransactionsTableSign>;
} = {
  [Columns.ASSET_NAME]: {
    id: Columns.ASSET_NAME,
    Header: 'Currency',
    accessor: 'asset',
    className: styles.currency,
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        <Currency
          asset={cell.value}
          walletKind={cell.row.original.walletKind}
          isTransaction={true}
        />
      </TransactionCell>
    ),
    cellTitle: (item?: PortfolioTransactionsTableSign['asset']) => item?.name,
  },
  [Columns.TYPE]: {
    id: Columns.TYPE,
    Header: 'Type',
    accessor: 'orderType',
    className: styles.type,
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value}
      </TransactionCell>
    ),
  },
  [Columns.ORDER_STATUS]: {
    id: Columns.ORDER_STATUS,
    Header: 'Status',
    accessor: 'orderStatus',
    className: styles.status,
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value}
      </TransactionCell>
    ),
  },
  [Columns.DATE]: {
    id: Columns.DATE,
    Header: 'Date',
    accessor: 'orderTimestamp',
    className: styles.date,
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value}
      </TransactionCell>
    ),
  },
  [Columns.USD_AMOUNT]: {
    id: Columns.USD_AMOUNT,
    Header: 'USD amount',
    accessor: 'baseAsset',
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value.amount !== 0 ? <USDAmount amount={cell.value.amount} /> : '\u2013'}
      </TransactionCell>
    ),
    cellTitle: (item?: PortfolioTransactionsTableSign['baseAsset']) =>
      item?.amount.toString(),
  },
  [Columns.ASSET_AMOUNT]: {
    id: Columns.ASSET_AMOUNT,
    Header: 'Crypto amount',
    accessor: 'asset',
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value.assetFormat !== AssetFormat.FIAT && cell.value.amount !== 0 ? (
          <CryptoAmount asset={cell.value} />
        ) : (
          '\u2013'
        )}
      </TransactionCell>
    ),
    cellTitle: (item?: PortfolioTransactionsTableSign['asset']) =>
      item?.amount.toString(),
  },
  [Columns.QUOTED_PRICE]: {
    id: Columns.QUOTED_PRICE,
    Header: 'Target price',
    accessor: 'quotedPrice',
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {formatUSD(cell.value)}
      </TransactionCell>
    ),
  },
  [Columns.MADE_BY]: {
    id: Columns.MADE_BY,
    Header: 'Made by',
    accessor: 'madeBy',
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value}
      </TransactionCell>
    ),
  },
  [Columns.CHARGED_FROM]: {
    id: Columns.CHARGED_FROM,
    Header: 'Charged from',
    accessor: 'chargedFrom',
    Cell: ({ cell }) => (
      <TransactionCell
        orderStatus={cell.row.original.orderStatus}
        isUserRelated={!!cell.row.original.madeBy}
      >
        {cell.value}
      </TransactionCell>
    ),
  },
};

export const getColumns = (cols: TransactionsColumnsType[]) =>
  cols.map((colId) => columns[colId]);
